// src/pages/Translations.js
//
// The admin screen the user asked for: "نظام نختار صفحات النظام ونترجم كل
// نص فيها حسب اللغة" — pick a page (namespace), then edit every string on
// it for one language at a time. Reuses language_view/language_edit (same
// as the Languages CRUD page) rather than inventing a new permission, per
// internal/handlers/translation_handler.go's own doc comment.
//
// Two-step flow: a namespace picker (ListTranslationNamespacesHandler,
// showing how many keys each non-default language has actually translated
// so an admin can see progress at a glance) → an edit grid for one chosen
// namespace + one chosen language at a time (GetNamespaceTranslationsHandler
// returns every key's English default plus what's saved so far for every
// language in one call, so switching the language tab doesn't refetch).
// Leaving a field blank and saving reverts that key to the English
// fallback — it does not save an empty string (see
// SaveNamespaceTranslationsHandler's own doc comment) — so a "Reset" per
// row is just "clear the field then Save".
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  getTranslationNamespaces,
  getNamespaceTranslations,
  saveNamespaceTranslations,
  getLanguages,
  exportTranslations,
  importTranslations,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import SelectBox from "../components/UI/SelectBox";
import PageHeader from "../components/UI/PageHeader";
import NamespaceTranslationsButton from "../components/UI/NamespaceTranslationsButton";
import { notify } from "../utils/notify";
import { AnimatePresence, motion } from "framer-motion";

export default function Translations() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { userPermissions } = useContext(AuthContext);
  const canEdit = userPermissions?.includes("language_edit");

  const [namespaces, setNamespaces] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [activeNamespace, setActiveNamespace] = useState(null);
  const [detail, setDetail] = useState(null); // { namespace, languages, keys }
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeLangCode, setActiveLangCode] = useState(null);
  const [draft, setDraft] = useState({}); // key -> text (current language only)
  const [saving, setSaving] = useState(false);

  // --- Bulk import/export: one CSV covering every namespace/key for a
  // single language, instead of clicking through the edit grid above one
  // namespace at a time. Both share one modal (transferMode picks which),
  // and both need the SAME non-default language list the edit grid itself
  // already restricts to (see GetNamespaceTranslationsHandler) — English's
  // text comes from code, not from a saved translation, so there's nothing
  // to export/import for it.
  const [languages, setLanguages] = useState([]);
  const [transferMode, setTransferMode] = useState(null); // null | "export" | "import"
  const [transferLangCode, setTransferLangCode] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [transferBusy, setTransferBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    getLanguages(token)
      .then((langs) => setLanguages((langs || []).filter((l) => !l.is_default)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeTransferModal = () => {
    if (transferBusy) return;
    setTransferMode(null);
    setTransferLangCode(null);
    setImportFile(null);
  };

  const handleExport = async () => {
    if (!transferLangCode) return;
    setTransferBusy(true);
    try {
      const blob = await exportTranslations(token, transferLangCode);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `translations_${transferLangCode}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      notify.success("Export ready");
      closeTransferModal();
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to export translations");
    } finally {
      setTransferBusy(false);
    }
  };

  const handleImport = async () => {
    if (!transferLangCode || !importFile) return;
    setTransferBusy(true);
    try {
      const result = await importTranslations(token, transferLangCode, importFile);
      notify.success(`Imported ${result.imported}, cleared ${result.deleted}, skipped ${result.skipped}`);
      closeTransferModal();
      fetchNamespaces(); // refresh progress bars with the newly imported counts
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to import translations");
    } finally {
      setTransferBusy(false);
    }
  };

  const fetchNamespaces = async () => {
    try {
      setLoadingList(true);
      const data = await getTranslationNamespaces(token);
      setNamespaces(data || []);
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to load pages");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { fetchNamespaces(); }, []);

  const filteredNamespaces = useMemo(
    () => namespaces.filter((n) => n.namespace.toLowerCase().includes(searchTerm.toLowerCase())),
    [namespaces, searchTerm]
  );

  const openNamespace = async (namespace) => {
    setActiveNamespace(namespace);
    setLoadingDetail(true);
    setDetail(null);
    try {
      const data = await getNamespaceTranslations(token, namespace);
      setDetail(data);
      const firstLang = data?.languages?.[0]?.code || null;
      setActiveLangCode(firstLang);
      setDraft(buildDraft(data, firstLang));
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to load page translations");
      setActiveNamespace(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  function buildDraft(data, langCode) {
    const next = {};
    (data?.keys || []).forEach((k) => {
      next[k.key] = (langCode && k.values?.[langCode]) || "";
    });
    return next;
  }

  const switchLang = (code) => {
    setActiveLangCode(code);
    setDraft(buildDraft(detail, code));
  };

  const handleSave = async () => {
    if (!activeNamespace || !activeLangCode) return;
    setSaving(true);
    try {
      const items = Object.entries(draft).map(([key, text]) => ({ key, text }));
      await saveNamespaceTranslations(token, activeNamespace, { language_code: activeLangCode, items });
      notify.success("Translations saved");
      // Refresh so the namespace list's own progress counts and this
      // language's "translated" state (used for the untranslated/English
      // placeholder) reflect what was just saved.
      const data = await getNamespaceTranslations(token, activeNamespace);
      setDetail(data);
      setDraft(buildDraft(data, activeLangCode));
      fetchNamespaces();
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to save translations");
    } finally {
      setSaving(false);
    }
  };

  // Re-fetch the open namespace's own edit grid after a scoped CSV import
  // (NamespaceTranslationsButton below) lands new rows straight into the
  // database — same refresh handleSave already does after a manual save,
  // just triggered from the import side instead.
  const refreshActiveNamespace = async () => {
    if (!activeNamespace) return;
    try {
      const data = await getNamespaceTranslations(token, activeNamespace);
      setDetail(data);
      setDraft(buildDraft(data, activeLangCode));
      fetchNamespaces();
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to refresh page translations");
    }
  };

  // --- Namespace picker ---
  if (!activeNamespace) {
    return (
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
        <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
              <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
            </button>
            <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
              <ICONS.Geography size={40} className="shrink-0" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>Translations</h2>
              <p className="text-gray-400 text-xs md:text-sm font-medium">Pick a page to translate its text into another language</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search pages..."
            />
            <Button variant="outline" icon={ICONS.Download} onClick={() => setTransferMode("export")}>
              Export
            </Button>
            {canEdit && (
              <Button variant="outline" icon={ICONS.Install} onClick={() => setTransferMode("import")}>
                Import
              </Button>
            )}
          </div>
        </PageHeader>

        {loadingList ? (
          <div className="text-center text-gray-400 py-16 animate-pulse">Loading pages...</div>
        ) : filteredNamespaces.length === 0 ? (
          <div className="text-center text-gray-400 py-16 italic">No translatable pages found.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredNamespaces.map((ns) => (
              <button
                key={ns.namespace}
                onClick={() => openNamespace(ns.namespace)}
                className="text-start bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-md hover:border-[var(--primary-color)]/20 transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-black text-gray-800 capitalize">{ns.namespace}</h3>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{ns.key_count} strings</span>
                </div>
                <div className="space-y-2">
                  {(ns.progress || []).map((p) => {
                    const pct = ns.key_count > 0 ? Math.round((p.translated / ns.key_count) * 100) : 0;
                    return (
                      <div key={p.code}>
                        <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                          <span className="font-bold">{p.name}</span>
                          <span>{p.translated}/{ns.key_count}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-[var(--primary-color)] transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {(!ns.progress || ns.progress.length === 0) && (
                    <p className="text-[11px] text-gray-300 italic">No additional languages yet.</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        <AnimatePresence>
          {transferMode && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeTransferModal}
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
              />

              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 15 }}
                className="relative bg-white rounded-[2.5rem] p-8 max-w-lg w-full shadow-2xl z-10"
              >
                <h3 className="text-xl font-black text-gray-900 mb-1">
                  {transferMode === "export" ? "Export Translations" : "Import Translations"}
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  {transferMode === "export"
                    ? "Downloads one file covering every page's strings for the language you pick — filled in wherever a translation already exists, blank everywhere else."
                    : "Pick the language the file is for, then upload a CSV built by Export (or matching its namespace/key/translation columns) to bulk-save every row it contains."}
                </p>

                {languages.length === 0 ? (
                  <div className="text-center text-gray-400 py-6 italic">
                    No additional languages yet — add one from the Languages page first.
                  </div>
                ) : (
                  <div className="space-y-4">
                    <SelectBox
                      label="Language"
                      name="transfer_language"
                      options={languages.map((l) => ({ value: l.code, label: l.name }))}
                      value={transferLangCode}
                      onChange={setTransferLangCode}
                      placeholder="Choose a language…"
                    />

                    {transferMode === "import" && (
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv"
                          className="hidden"
                          onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full text-sm text-gray-600 bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-200 hover:border-[var(--primary-color)] transition-all px-4 py-3 text-start"
                        >
                          {importFile ? importFile.name : "Choose a CSV file…"}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3 mt-8">
                  <Button variant="secondary" onClick={closeTransferModal} className="flex-1 !rounded-2xl" disabled={transferBusy}>
                    Cancel
                  </Button>
                  {languages.length > 0 && (
                    <Button
                      variant="primary"
                      onClick={transferMode === "export" ? handleExport : handleImport}
                      isLoading={transferBusy}
                      className="flex-1 !rounded-2xl"
                      disabled={!transferLangCode || (transferMode === "import" && !importFile)}
                    >
                      {transferMode === "export" ? "Export" : "Import"}
                    </Button>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // --- Edit grid for one namespace ---
  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => { setActiveNamespace(null); setDetail(null); }}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400"
          >
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Geography size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold capitalize" style={{ color: "var(--primary-color)" }}>{activeNamespace}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">Blank text falls back to showing the English default</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <NamespaceTranslationsButton namespace={activeNamespace} onImported={refreshActiveNamespace} />
          {canEdit && (
            <Button variant="primary" icon={ICONS.Save} onClick={handleSave} isLoading={saving} disabled={!activeLangCode}>
              Save Translations
            </Button>
          )}
        </div>
      </PageHeader>

      {loadingDetail || !detail ? (
        <div className="text-center text-gray-400 py-16 animate-pulse">Loading...</div>
      ) : (detail.languages || []).length === 0 ? (
        <div className="text-center text-gray-400 py-16 italic">
          No additional languages to translate into yet — add one from the Languages page first.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-6">
            {detail.languages.map((l) => (
              <button
                key={l.code}
                onClick={() => switchLang(l.code)}
                className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wide transition-colors ${
                  activeLangCode === l.code
                    ? "bg-[var(--primary-color)] text-white shadow-sm"
                    : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                }`}
              >
                {l.name}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm divide-y divide-gray-50">
            {detail.keys.map((k) => (
              <div key={k.key} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wide text-gray-400 font-mono mb-1">{k.key}</p>
                  <p className="text-sm text-gray-700">{k.english_text}</p>
                  {k.description && <p className="text-[11px] text-gray-400 italic mt-1">{k.description}</p>}
                </div>
                <div>
                  <textarea
                    disabled={!canEdit}
                    value={draft[k.key] ?? ""}
                    onChange={(e) => setDraft((prev) => ({ ...prev, [k.key]: e.target.value }))}
                    placeholder={k.english_text}
                    rows={2}
                    className="w-full text-sm text-gray-900 bg-gray-50/50 rounded-xl border-2 border-gray-100 focus:border-[var(--primary-color)] transition-all outline-none focus:outline-none focus:ring-0 px-4 py-2 disabled:opacity-60"
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
